import { useContext, useEffect, useState, useRef } from "react";
import {
  ChevronUp,
  ChevronDown,
  ChevronDoubleDown,
  ChevronDoubleUp,
  Trash,
  TriangleFill,
  CircleFill,
  Fonts,
  SquareFill,
  Image,
  Gear,
  Brush,
  ArrowCounterclockwise,
  ArrowClockwise,
  Magnet,
  Grid3x3,
} from "react-bootstrap-icons";
import {
  BiObjectsHorizontalLeft,
  BiObjectsHorizontalCenter,
  BiObjectsHorizontalRight,
  BiObjectsVerticalTop,
  BiObjectsVerticalCenter,
  BiObjectsVerticalBottom,
} from "react-icons/bi";
import { BsQrCodeScan } from "react-icons/bs";
import { AppContext } from "../context/AppContext";
import { createDefaultObject, ObjectType } from "@utils/utils";

const ICON_SIZE = 16;

type Props = {
  isSaving: boolean;
  toShowSaveTemplateButton: boolean;
  onSaveTemplate: () => void;
  onTemplateSettingsClick: () => void;
  onRenderClick: () => void;
  onAddImageClick: () => void;
  onShortcutClick: () => void;
};

function Toolbars({
  isSaving,
  toShowSaveTemplateButton,
  onSaveTemplate = () => {},
  onTemplateSettingsClick = () => {},
  onRenderClick = () => {},
  onAddImageClick = () => {},
  onShortcutClick = () => {},
}: Props) {
  const { coreHandler, selectedObject } = useContext(AppContext);
  const [isAnyObjectSelected, setIsAnyObjectSelected] = useState(false);
  const [isRedoDisabled, setIsRedoDisabled] = useState(true);
  const [isUndoDisabled, setIsUndoDisabled] = useState(true);
  const [isSnappingEnabled, setIsSnappingEnabled] = useState(false);
  const [isGridEnabled, setIsGridEnabled] = useState(false);
  const [gridSize, setGridSize] = useState(20);
  const [showGridSizeSlider, setShowGridSizeSlider] = useState(false);
  const gridSliderRef = useRef<HTMLDivElement>(null);

  function addAndRenderObject(type: ObjectType) {
    const object = createDefaultObject(type);
    coreHandler?.add(object);
  }

  function toggleSnapping() {
    if (coreHandler?.snappingHandler) {
      if (isSnappingEnabled) {
        coreHandler.snappingHandler.disable();
      } else {
        // Disable grid when enabling snapping
        if (isGridEnabled && coreHandler.gridHandler) {
          coreHandler.gridHandler.disable();
        }
        coreHandler.snappingHandler.enable();
      }
    }
  }

  function toggleGrid() {
    if (coreHandler?.gridHandler) {
      if (isGridEnabled) {
        coreHandler.gridHandler.disable();
        setShowGridSizeSlider(false);
      } else {
        // Disable snapping when enabling grid
        if (isSnappingEnabled && coreHandler.snappingHandler) {
          coreHandler.snappingHandler.disable();
        }
        coreHandler.gridHandler.enable();
        setShowGridSizeSlider(true);
      }
    }
  }

  function handleGridSizeChange(newSize: number) {
    setGridSize(newSize);
    if (coreHandler?.gridHandler) {
      coreHandler.gridHandler.setGridSize(newSize);
    }
  }

  useEffect(() => {
    setIsAnyObjectSelected(!!selectedObject);
  }, [selectedObject]);

  useEffect(() => {
    coreHandler?.transactionHandler.on("change", () => {
      setIsRedoDisabled(coreHandler.transactionHandler.redos.length === 0);
      setIsUndoDisabled(coreHandler.transactionHandler.undos.length === 0);
    });
  }, [coreHandler?.transactionHandler]);

  useEffect(() => {
    if (coreHandler?.snappingHandler) {
      setIsSnappingEnabled(coreHandler.snappingHandler.isEnabled);

      coreHandler.snappingHandler.on("snapping:changed", (enabled: boolean) => {
        setIsSnappingEnabled(enabled);
      });
    }
  }, [coreHandler?.snappingHandler]);

  useEffect(() => {
    if (coreHandler?.gridHandler) {
      setIsGridEnabled(coreHandler.gridHandler.isEnabled);
      setGridSize(coreHandler.gridHandler.getGridSize());

      coreHandler.gridHandler.on("grid:changed", (enabled: boolean) => {
        setIsGridEnabled(enabled);
        setShowGridSizeSlider(enabled);
      });

      coreHandler.gridHandler.on("grid:size-changed", (size: number) => {
        setGridSize(size);
      });
    }
  }, [coreHandler?.gridHandler]);

  // Close grid slider when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        gridSliderRef.current &&
        !gridSliderRef.current.contains(event.target as Node)
      ) {
        setShowGridSizeSlider(false);
      }
    }

    if (showGridSizeSlider) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showGridSizeSlider]);

  return (
    <div className="flex items-center justify-between border-b border-gray-200 bg-white px-5 py-1.5 shadow-sm">
      <div className="flex items-center space-x-1">
        <button
          className="rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
          data-tip="QRcode"
          onClick={() => {
            addAndRenderObject("qrcode");
          }}
        >
          <BsQrCodeScan size={ICON_SIZE} />
        </button>
        <button
          className="rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
          data-tip="Triangle (a)"
          onClick={() => {
            addAndRenderObject("triangle");
          }}
        >
          <TriangleFill size={ICON_SIZE} />
        </button>
        <button
          className="rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
          data-tip="Circle (c)"
          onClick={() => {
            addAndRenderObject("circle");
          }}
        >
          <CircleFill size={ICON_SIZE} />
        </button>
        <button
          className="rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
          data-tip="Rect (r)"
          onClick={() => {
            addAndRenderObject("rect");
          }}
        >
          <SquareFill size={ICON_SIZE} />
        </button>
        <button
          className="rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
          data-tip="Text (t)"
          onClick={() => {
            addAndRenderObject("text");
          }}
        >
          <Fonts size={ICON_SIZE} />
        </button>
        <button
          className="rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
          data-tip="Image"
          onClick={() => {
            onAddImageClick();
          }}
        >
          <Image size={ICON_SIZE} />
        </button>
        <div className="mx-2 h-6 w-px bg-gray-300"></div>
        <button
          className="rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:text-gray-300 disabled:hover:bg-transparent"
          data-tip="Bring Forward ( [ )"
          disabled={!isAnyObjectSelected}
          onClick={() => {
            coreHandler?.bringForward();
          }}
        >
          <ChevronUp size={ICON_SIZE} />
        </button>
        <button
          className="rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:text-gray-300 disabled:hover:bg-transparent"
          data-tip="Send Backward ( ] )"
          disabled={!isAnyObjectSelected}
          onClick={() => {
            coreHandler?.sendBackward();
          }}
        >
          <ChevronDown size={ICON_SIZE} />
        </button>
        <button
          className="rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:text-gray-300 disabled:hover:bg-transparent"
          data-tip="Bring to front"
          disabled={!isAnyObjectSelected}
          onClick={() => {
            coreHandler?.bringToFront();
          }}
        >
          <ChevronDoubleUp size={ICON_SIZE} />
        </button>
        <button
          className="rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:text-gray-300 disabled:hover:bg-transparent"
          data-tip="Send to back"
          disabled={!isAnyObjectSelected}
          onClick={() => {
            coreHandler?.sendToBack();
          }}
        >
          <ChevronDoubleDown size={ICON_SIZE} />
        </button>
        <div className="mx-2 h-6 w-px bg-gray-300"></div>
        <button
          className="rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:text-gray-300 disabled:hover:bg-transparent"
          data-tip="Align to Left Edge"
          disabled={!isAnyObjectSelected}
          onClick={() => {
            coreHandler?.alignToLeftEdge();
          }}
        >
          <BiObjectsHorizontalLeft size={ICON_SIZE} type="logo" />
        </button>
        <button
          className="rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:text-gray-300 disabled:hover:bg-transparent"
          data-tip="Align to Center Horizontal"
          disabled={!isAnyObjectSelected}
          onClick={() => {
            coreHandler?.alignToCenterHorizontal();
          }}
        >
          <BiObjectsHorizontalCenter size={ICON_SIZE} />
        </button>
        <button
          className="rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:text-gray-300 disabled:hover:bg-transparent"
          data-tip="Align to Right Edge"
          disabled={!isAnyObjectSelected}
          onClick={() => {
            coreHandler?.alignToRightEdge();
          }}
        >
          <BiObjectsHorizontalRight size={ICON_SIZE} />
        </button>
        <div className="mx-2 h-6 w-px bg-gray-300"></div>
        <button
          className="rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:text-gray-300 disabled:hover:bg-transparent"
          data-tip="Align to Top Edge"
          disabled={!isAnyObjectSelected}
          onClick={() => {
            coreHandler?.alignToTopEdge();
          }}
        >
          <BiObjectsVerticalTop size={ICON_SIZE} />
        </button>
        <button
          className="rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:text-gray-300 disabled:hover:bg-transparent"
          data-tip="Align to Center Vertical"
          disabled={!isAnyObjectSelected}
          onClick={() => {
            coreHandler?.alignToCenterVertical();
          }}
        >
          <BiObjectsVerticalCenter size={ICON_SIZE} />
        </button>
        <button
          className="rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:text-gray-300 disabled:hover:bg-transparent"
          data-tip="Align to Bottom Edge"
          disabled={!isAnyObjectSelected}
          onClick={() => {
            coreHandler?.alignToBottomEdge();
          }}
        >
          <BiObjectsVerticalBottom size={ICON_SIZE} />
        </button>

        <div className="mx-2 h-6 w-px bg-gray-300"></div>
        <button
          className="rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:text-gray-300 disabled:hover:bg-transparent"
          data-tip="Undo (meta + z)"
          disabled={isUndoDisabled}
          onClick={() => {
            coreHandler?.transactionHandler.undo();
          }}
        >
          <ArrowCounterclockwise size={ICON_SIZE} />
        </button>
        <button
          className="rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:text-gray-300 disabled:hover:bg-transparent"
          data-tip="Redo (shift + meta + z)"
          disabled={isRedoDisabled}
          onClick={() => {
            coreHandler?.transactionHandler.redo();
          }}
        >
          <ArrowClockwise size={ICON_SIZE} />
        </button>
        <div className="mx-2 h-6 w-px bg-gray-300"></div>
        <button
          className={`rounded-lg p-2.5 transition-colors ${
            isSnappingEnabled
              ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
              : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
          }`}
          data-tip="Toggle Snapping"
          onClick={toggleSnapping}
        >
          <Magnet size={ICON_SIZE} />
        </button>
        <div className="relative" ref={gridSliderRef}>
          <button
            className={`rounded-lg p-2.5 transition-colors ${
              isGridEnabled
                ? "bg-green-100 text-green-700 hover:bg-green-200"
                : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            }`}
            data-tip="Toggle Grid"
            onClick={toggleGrid}
          >
            <Grid3x3 size={ICON_SIZE} />
          </button>

          {showGridSizeSlider && (
            <div className="min-w-48 absolute top-full left-0 z-10 mt-2 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
              <div className="flex items-center space-x-2">
                <label className="whitespace-nowrap text-xs text-gray-600">
                  Grid Size:
                </label>
                <input
                  type="range"
                  min="20"
                  max="100"
                  value={gridSize}
                  onChange={(e) =>
                    handleGridSizeChange(parseInt(e.target.value))
                  }
                  className="slider h-2 flex-1 cursor-pointer appearance-none rounded-lg bg-gray-200"
                />
                <span className="min-w-8 text-xs text-gray-600">
                  {gridSize}px
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="mx-2 h-6 w-px bg-gray-300"></div>
        <button
          className="rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:text-gray-300 disabled:hover:bg-transparent"
          data-tip="Remove [backspace]"
          disabled={!isAnyObjectSelected}
          onClick={() => {
            coreHandler?.remove();
          }}
        >
          <Trash size={ICON_SIZE} />
        </button>
      </div>
      <div className="flex items-center space-x-3">
        {toShowSaveTemplateButton && (
          <div className="flex items-center rounded-lg border border-amber-200 bg-amber-50 px-2 py-1">
            <div className="mr-1.5 h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-700">
              Autosaving...
            </span>
          </div>
        )}
        <button
          className="flex items-center justify-center rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
          data-tip="Render"
          onClick={onRenderClick}
        >
          <Brush size={ICON_SIZE} />
        </button>
        <button
          className="flex items-center justify-center rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
          data-tip="Template Settings"
          onClick={onTemplateSettingsClick}
        >
          <Gear size={ICON_SIZE} />
        </button>
      </div>
    </div>
  );
}

export default Toolbars;
